var _excluded = ["component", "disabled", "label", "leadingArea", "role", "trailingArea", "value"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import useStyles from './use_styles';
import isVeneerIcon from '../utils/is_veneer_icon';
import propTypes from './prop_types';
import { useMenuListContext } from '../menu_list/menu_list_context';
import mergeRefs from 'react-merge-refs';
import { jsx as ___EmotionJSX } from "@emotion/core";
var MenuItem = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      component: Component = 'li',
      disabled = false,
      label,
      leadingArea,
      role = 'menuitem',
      trailingArea,
      value
    } = _ref,
    menuItemProps = _objectWithoutProperties(_ref, _excluded);
  var {
    onClick,
    onClose,
    highlightedValue,
    setHighlightedValue,
    selectedValue,
    leadingAreaWhiteSpace,
    menuItemCustomProps,
    highlightedRef,
    first,
    last,
    next,
    prev,
    search
  } = useMenuListContext();
  var isSelected = selectedValue === value;
  var itemRef = React.useRef();
  var handleRef = highlightedRef && highlightedValue === value ? mergeRefs([highlightedRef, itemRef, ref]) : mergeRefs([itemRef, ref]);
  React.useEffect(() => {
    if (highlightedValue === value) {
      var _itemRef$current;
      (_itemRef$current = itemRef.current) === null || _itemRef$current === void 0 || _itemRef$current.focus();
    }
  }, [highlightedValue, value, itemRef]);
  var style = useStyles({
    disabled,
    hasIcon: Boolean(leadingArea),
    isSelected,
    leadingAreaWhiteSpace
  });
  var renderedLeadingArea = React.useMemo(() => {
    if (isSelected && isVeneerIcon(leadingArea)) {
      return /*#__PURE__*/React.cloneElement(leadingArea, {
        filled: true
      });
    }
    return leadingArea;
  }, [isSelected, leadingArea]);
  var handleClick = React.useCallback(event => {
    if (disabled) {
      return;
    }
    setHighlightedValue(value);
    onClick === null || onClick === void 0 || onClick(event, {
      disabled,
      value,
      label
    });
  }, [disabled, setHighlightedValue, value, onClick, label]);
  var handleKeyDown = React.useCallback(event => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        handleClick(event);
        break;
      case 'Tab':
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        onClose === null || onClose === void 0 || onClose(event);
        break;
      case 'End':
        last();
        break;
      case 'Home':
        first();
        break;
      case 'Up': // IE compat
      case 'ArrowUp':
        event.preventDefault();
        prev();
        break;
      case 'Down': // IE compat
      case 'ArrowDown':
        event.preventDefault();
        next();
        break;
      default:
        search(event.key);
        event.stopPropagation();
        break;
    }
  }, [first, handleClick, last, next, onClose, prev, search]);
  var handleMouseDown = React.useCallback(event => {
    if (disabled) {
      event.preventDefault();
    }
  }, [disabled]);
  return ___EmotionJSX(Component, _extends({
    "aria-disabled": disabled,
    css: style.menuItem,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onMouseDown: handleMouseDown,
    ref: handleRef,
    role: role,
    tabIndex: disabled ? -1 : 0
  }, menuItemProps, menuItemCustomProps), leadingArea && ___EmotionJSX("span", {
    css: style.leadingArea
  }, renderedLeadingArea), ___EmotionJSX("span", {
    css: style.label
  }, label || value), trailingArea && ___EmotionJSX("span", {
    css: style.trailingArea
  }, trailingArea));
});
MenuItem.propTypes = propTypes;
MenuItem.displayName = 'MenuItem';
export default MenuItem;